import { Dialog, Transition } from '@headlessui/react'
import { Fragment, useEffect, useState } from 'react'
import { IListing } from './Home';
import { fetchApartmentsListingsData } from './DataServices/ApartmentsListingsDataServices';
import { CloseOutlined, PersonOutlineRounded } from '@mui/icons-material';
import { IAutomaticResponseHistory, IListingToProspectiveTenant, ITransferTenantDetails } from './Models';
import Loading from './Loading';
import Notification from './Notification';
import { RejectConfirmAppointment } from './DataServices/AppointmentsDataServices';
import { TransferTenantToAppartmentService } from './DataServices/ProspectiveTenantDataService';

interface TransferTenantToAppartmentProps {
    open: boolean;
    setOpen: React.Dispatch<React.SetStateAction<boolean>>;
    selectedTenants: IAutomaticResponseHistory[];
}


const TransferTenantToAppartment = ({ open, setOpen, selectedTenants }: TransferTenantToAppartmentProps) => {
    const [listings, setListings] = useState<IListing[]>([]);
    const [loading, setLoading] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState("");
    const [notificationMessageType, setNotificationMessageType] = useState("success");
    const [openNotification, setOpenNotification] = useState(false);

    useEffect(() => {
        async function fetchData() {
            try {
                const data = await fetchApartmentsListingsData(true);
                // First, set the listings with the fetched data
                setListings(data);

                // Then immediately filter out the ones in selectedTenants
                setListings(prevListings => prevListings.filter(l => !selectedTenants.some(t => t.title === l.title)));
            } catch (error) {
                console.error("Error fetching data:", error);
            }
        }

        fetchData();
    }, [open, selectedTenants]);

    const handleTransferTenantsToAppartment = async (selectedList: IListing) => {
        setLoading(true);
        try {
            const newList:any = [];
            const transferTenantDetails: ITransferTenantDetails[] = [];
            const arhIds: string[] = [];
            selectedTenants.forEach(st => {
                newList.push({
                    id: '',
                    ListingID: st.listingID,
                    ProspectiveTenantID: st.idProspectiveTenant,
                    NewListingID:selectedList.id,
                    isDeleted: false
                })
                transferTenantDetails.push({
                    arhId: st.id,
                    newApartmentID: selectedList.idApartment,
                    tenantEmail: st.email
                })
                arhIds.push(st.id);
            })

           
            await RejectConfirmAppointment(arhIds, false);
            await TransferTenantToAppartmentService(transferTenantDetails);

         
            setLoading(false);
            setOpen(false);
            setOpenNotification(true);
            setNotificationMessage("Tenants transfered successfully!");
            setNotificationMessageType("success");
            setTimeout(() => {
                setOpenNotification(false);
            }, 3000);
        } catch (error) {
            console.log("Error: ", error);
            setLoading(false);
            setOpenNotification(true);
            setNotificationMessage("Tenants transfered failed!");
            setNotificationMessageType("error");
            setTimeout(() => {
                setOpenNotification(false);
            }, 3000);
        }

    }

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={setOpen}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                    </Transition.Child>
                    <div className="fixed inset-0 z-15 w-screen overflow-y-auto">
                        <div className="flex min-h-full min-w-full justify-center text-center items-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[90%] max-h-[80vh] p-4 overflow-y-auto">
                                    <div className='flex items-center justify-between gap-2'>
                                        <h2 className='font-semibold text-gray-700 p-2'> </h2>
                                        <span
                                            className='cursor-pointer active:scale-[0.98]'
                                            onClick={() => setOpen(false)}>
                                            <CloseOutlined fontSize='small' />
                                        </span>
                                    </div>
                                    <div className='flex flex-col items-center justify-center mb-4'>
                                        <h2 className='text-2xl font-semibold mb-1'>Transfer tenants to another appartment</h2>
                                        <h4 className='text-sm text-gray-400'>(To move the tenants into the apartment, simply click the home icon.)</h4>
                                    </div>
                                    <div className='mb-2'>
                                        <div className='flex items-center justify-start gap-2'>
                                            <h2 className='font-semibold text-gray-700 p-2'>Tenants</h2>
                                        </div>
                                        <div className='flex items-center flex-wrap gap-3 mx-2'>
                                            {
                                                selectedTenants && selectedTenants.map((st) => {
                                                    return (
                                                        <div className='flex items-center justify-center gap-2 p-2 rounded-md border'>
                                                            <div className='flex text-gray-400 items-center justify-center w-12 h-12 rounded-full border'>
                                                                <PersonOutlineRounded fontSize='large' />
                                                            </div>
                                                            <div>
                                                                <h1 className='font-medium'>{st.email}</h1>
                                                                <h2 className='text-sm text-gray-400'>{st.title}</h2>
                                                            </div>
                                                        </div>
                                                    )
                                                })
                                            }
                                        </div>
                                    </div>
                                    <div className='mb-2'>
                                        <h2 className='font-semibold text-gray-700 p-2'>Available Appartments</h2>
                                        <div className="relative overflow-x-auto shadow-md border rounded-lg mx-2">
                                            <table className="w-full text-sm text-left rtl:text-right text-gray-600">
                                                <thead className="text-xs text-gray-700 uppercase bg-gray-100">
                                                    <tr>
                                                        <th scope="col" className="px-6 py-3">
                                                            Title
                                                        </th>
                                                        <th scope="col" className="px-6 py-3">
                                                            Building
                                                        </th>
                                                        <th scope="col" className="px-6 py-3">
                                                            Rental Price
                                                        </th>
                                                        <th scope="col" className="px-6 py-3">
                                                            Current Price
                                                        </th>
                                                        <th scope="col" className="px-6 py-3">
                                                            Move In Date
                                                        </th>
                                                        <th scope="col" className="px-6 py-3">
                                                            No. of Bedrooms
                                                        </th>
                                                        <th scope="col" className="px-6 py-3 truncate">
                                                            Move Here
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        listings.map((l) => {
                                                            return (
                                                                <tr key={l.id} className='border-b'>
                                                                    <td className="px-6 py-3 truncate">
                                                                        {l.title}
                                                                    </td>
                                                                    <td className="px-6 py-3 truncate">
                                                                        {l.building}
                                                                    </td>
                                                                    <td className="px-6 py-3 truncate">
                                                                        {l.rent}
                                                                    </td>
                                                                    <td className="px-6 py-3 truncate">
                                                                        {l.currentRent}
                                                                    </td>
                                                                    <td className="px-6 py-3 truncate">
                                                                        {l.moveInDate ? new Date(l.moveInDate).toLocaleDateString('en-US', {
                                                                            year: 'numeric',
                                                                            month: 'long',
                                                                            day: 'numeric',
                                                                        }) : ""}
                                                                    </td>
                                                                    <td className="px-6 py-3 text-center truncate">
                                                                        {l.numOfBedrooms}
                                                                    </td>
                                                                    <td className="px-6 py-3 cursor-pointer  active:scale-95 flex items-center justify-center flex-nowrap truncate scale-90 hover:scale-100" onClick={() => handleTransferTenantsToAppartment(l)}>
                                                                        <svg
                                                                            xmlns="http://www.w3.org/2000/svg"
                                                                            viewBox="0 0 24 24"
                                                                            width="14"
                                                                            height="20"
                                                                        >
                                                                            <path
                                                                                d="M4 12h16M14 6l6 6-6 6"
                                                                                fill="blue"
                                                                                stroke="currentColor"
                                                                                stroke-width="2"
                                                                                stroke-linecap="round"
                                                                                stroke-linejoin="round"
                                                                            />
                                                                        </svg>
                                                                        <svg xmlns="http://www.w3.org/2000/svg" x="0px" y="0px" width="24" height="24" viewBox="0 0 24 24" className=' text-blue-500' fill='blue'>
                                                                            <path d="M 12 2.0996094 L 1 12 L 4 12 L 4 21 L 11 21 L 11 15 L 13 15 L 13 21 L 20 21 L 20 12 L 23 12 L 12 2.0996094 z M 12 4.7910156 L 18 10.191406 L 18 11 L 18 19 L 15 19 L 15 13 L 9 13 L 9 19 L 6 19 L 6 10.191406 L 12 4.7910156 z"></path>
                                                                        </svg>
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    }
                                                </tbody>
                                            </table>
                                        </div>

                                    </div>
                                    {
                                        loading && <div className="absolute bg-gray-300/80 top-0 left-0 w-full h-full flex items-center justify-center">
                                            <Loading />
                                        </div>
                                    }
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>
            <Notification
                isOpen={openNotification}
                setIsOpen={setOpenNotification}
                message={notificationMessage}
                messageType={notificationMessageType}
            />
        </>
    )
}

export default TransferTenantToAppartment
